import React, { useState, useEffect } from 'react';
import { Property, Category } from '../types';
import apiService from '../services/api';
import { PropertyCard } from '../components/property/PropertyCard';
import { PropertyCardSkeleton } from '../components/property/PropertyCardSkeleton';
import { Pagination } from '../components/ui/Pagination';
import { Filter, Search } from 'lucide-react';

const api = apiService;

export const PropertiesPage: React.FC = () => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [categories, setCategories] = useState<Category[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([fetchCategories(), fetchProperties()]);
      } catch (err) {
        console.error('Error loading properties:', err);
        setError('Failed to load properties. Please try again.');
      }
    };

    loadData();
  }, [page, selectedCategory, selectedStatus]);

  const fetchCategories = async () => {
    try {
      const response = await api.getCategories();
      if (response.success && response.data) {
        setCategories(response.data);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const params: any = { page };
      
      if (selectedCategory !== 'all') {
        params.category_id = parseInt(selectedCategory, 10);
      }
      
      if (selectedStatus !== 'all') {
        params.status = selectedStatus;
      }
      
      const response = await api.getProperties(params);
      if (response.data) {
        setProperties(response.data.data);
        setTotalPages(response.data.pagination.total_pages);
      }
      setError(null);
    } catch (err) {
      setError('Failed to fetch properties');
      console.error('Error fetching properties:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredProperties = properties.filter(property =>
    property.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    property.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    property.category?.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header Section */}
      <div className="flex flex-col gap-4 mb-8">
        {/* Title */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">Featured Properties</h1>
        </div>

        {/* Compact Filters */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
          {/* Category Filter */}
          <div className="w-full sm:w-48">
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors bg-white"
            >
              <option value="all">All Categories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div className="w-full sm:w-48">
            <select
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors bg-white"
            >
              <option value="all">All Status</option>
              <option value="for_sale">For Sale</option>
              <option value="for_rent">For Rent</option>
              <option value="residential_plots">Residential Plots</option>
              <option value="commercial_plots">Commercial Plots</option>
              <option value="agricultural_land">Agricultural Land</option>
              <option value="sold">Sold</option>
              <option value="rented">Rented</option>
              <option value="under_construction">Under Construction</option>
            </select>
          </div>

          {/* Clear Filters Button */}
          {(selectedCategory !== 'all' || selectedStatus !== 'all') && (
            <button
              onClick={() => {
                setSelectedCategory('all');
                setSelectedStatus('all');
                setPage(1);
              }}
              className="w-full sm:w-auto px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Clear
            </button>
          )}
        </div>

        {/* Search Bar */}
        <div className="flex justify-center">
          <div className="relative w-full sm:w-96 max-w-md">
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search properties..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Active Filters & Results Count */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          {/* Active Filters */}
          {(selectedCategory !== 'all' || selectedStatus !== 'all') && (
            <div className="flex flex-wrap gap-2 items-center justify-center sm:justify-start">
              <span className="text-sm text-gray-600">Filters:</span>
              {selectedCategory !== 'all' && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                  {categories.find(c => c.id.toString() === selectedCategory)?.name || 'Category'}
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className="ml-1 hover:text-red-600"
                  >
                    ×
                  </button>
                </span>
              )}
              {selectedStatus !== 'all' && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {selectedStatus.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  <button
                    onClick={() => setSelectedStatus('all')}
                    className="ml-1 hover:text-red-600"
                  >
                    ×
                  </button>
                </span>
              )}
            </div>
          )}
          
          {/* Results Count */}
          {!loading && (
            <div className="text-center sm:text-right">
              <span className="text-sm text-gray-600 font-medium">{filteredProperties.length} properties found</span>
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Properties Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
            <PropertyCardSkeleton key={n} />
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProperties.map((property) => (
              <PropertyCard 
                key={property.id} 
                property={property}
                showFeaturedBadge={property.is_featured}
              />
            ))}
          </div>
          
          {filteredProperties.length === 0 && (
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-gray-900">No properties found</h3>
              <p className="mt-2 text-gray-500">Try adjusting your search terms</p>
            </div>
          )}
        </>
      )}

      {/* Pagination */}
      {totalPages > 1 && !loading && (
        <div className="mt-8">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
 
};